import React, { memo, useState } from 'react';
import { AlertTriangle, Edit3, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, addToast, useDisclosure } from '@heroui/react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import jobsApi from '@/services/apis/jobs';
import guidesApi from '@/services/apis/guides';
import { JobStatus } from '@/types';
import { guideKeys } from '@/hooks/useGuides';
import ConfirmationModal from '@/components/base/ConfirmationModal';

interface GuideJobFailedProps {
  jobId: string;
  guideId: string;
  errorMessage?: string | null;
}

const GuideJobFailed: React.FC<GuideJobFailedProps> = memo(({ jobId, guideId, errorMessage }) => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const updateJobStatusMutation = useMutation({
    mutationFn: (status: JobStatus) => jobsApi.updateJobStatus(jobId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
    },
  });

  const deleteGuideMutation = useMutation({
    mutationFn: () => guidesApi.deleteGuide(guideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guideKeys.stats() });
      addToast({
        title: t('guides.builder.jobFailed.deleteSuccess'),
        color: 'success',
      });
      navigate('/admin/guides');
    },
    onError: (error: Error) => {
      addToast({
        title: error.message || t('guides.builder.jobFailed.deleteError'),
        color: 'danger',
      });
    },
  });

  const handleEditManually = async () => {
    setIsUpdating(true);
    try {
      await updateJobStatusMutation.mutateAsync(JobStatus.MANUALLY_RESOLVED);
      // The query will be invalidated and refetched, which will show the editor
      addToast({
        title: t('guides.builder.jobFailed.editSuccess'),
        color: 'success',
      });
    } catch (error) {
      addToast({
        title: error instanceof Error ? error.message : t('guides.builder.jobFailed.editError'),
        color: 'danger',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    onDeleteModalOpen();
  };

  const handleConfirmDelete = () => {
    deleteGuideMutation.mutate();
    onDeleteModalClose();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-md w-full mx-auto p-8 text-center">
        {/* Error Icon */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full bg-red-100 dark:bg-red-900/20"></div>
          <div className="absolute inset-2 rounded-full bg-red-50 dark:bg-red-900/10 flex items-center justify-center">
            <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />
          </div>
        </div>

        {/* Error Message */}
        <h2 className="text-2xl font-semibold text-foreground mb-2">{t('guides.builder.jobFailed.title')}</h2>
        <p className="text-muted-foreground mb-4">{t('guides.builder.jobFailed.description')}</p>

        {/* Error Details if available */}
        {errorMessage && (
          <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-left">
            <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
              {t('guides.builder.jobFailed.errorDetails')}:
            </p>
            <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            color="primary"
            onPress={handleEditManually}
            isLoading={isUpdating || updateJobStatusMutation.isPending}
            startContent={!isUpdating && !updateJobStatusMutation.isPending && <Edit3 className="w-4 h-4" />}
            disabled={isUpdating || updateJobStatusMutation.isPending || deleteGuideMutation.isPending}
          >
            {isUpdating || updateJobStatusMutation.isPending
              ? t('guides.builder.jobFailed.updating')
              : t('guides.builder.jobFailed.editManually')}
          </Button>
          <Button
            color="danger"
            variant="bordered"
            onPress={handleDelete}
            isLoading={deleteGuideMutation.isPending}
            startContent={!deleteGuideMutation.isPending && <Trash2 className="w-4 h-4" />}
            disabled={isUpdating || updateJobStatusMutation.isPending || deleteGuideMutation.isPending}
          >
            {deleteGuideMutation.isPending
              ? t('guides.builder.jobFailed.deleting')
              : t('guides.builder.jobFailed.deleteGuide')}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        onConfirm={handleConfirmDelete}
        title={t('guides.builder.jobFailed.deleteModalTitle')}
        message={t('guides.builder.jobFailed.deleteConfirm')}
        confirmLabel={t('guides.builder.jobFailed.deleteGuide')}
        cancelLabel={t('guides.builder.jobFailed.cancel')}
        confirmColor="danger"
        isLoading={deleteGuideMutation.isPending}
      />
    </div>
  );
});

GuideJobFailed.displayName = 'GuideJobFailed';

export default GuideJobFailed;
